import { Injectable, inject, signal } from '@angular/core';
import { CompanyApiService } from './company-api.service';
import {
  CreateCompanyOrganisationalLevelRequest,
  CompanyNamedLookupRequest,
  CompanyNamedLookupResponse,
  CompanyOrganisationalLevelResponse,
  CompanyRequest,
  CompanyResponse,
  UpdateCompanyOrganisationalLevelRequest,
} from './company.model';

@Injectable({ providedIn: 'root' })
export class CompanyStore {
  private readonly companyApiService = inject(CompanyApiService);

  readonly companies = signal<CompanyResponse[]>([]);
  readonly divisions = signal<CompanyNamedLookupResponse[]>([]);
  readonly jobTitles = signal<CompanyNamedLookupResponse[]>([]);
  readonly organisationalLevels = signal<CompanyOrganisationalLevelResponse[]>([]);
  readonly selectedCompanyId = signal<string | null>(null);
  readonly selectedCompany = signal<CompanyResponse | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly deactivating = signal(false);
  readonly activating = signal(false);

  private refreshSelectedCompany(companyId: string): void {
    this.loadCompanies();
    if (this.selectedCompanyId() === companyId) {
      this.selectCompany(companyId);
    }
  }

  loadCompanies(): void {
    this.loading.set(true);
    this.companyApiService.getCompanies({includeInactive: true}).subscribe({
      next: (companies) => {
        this.companies.set(companies);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  selectCompany(companyId: string | null): void {
    this.selectedCompanyId.set(companyId);
    if (!companyId) {
      this.selectedCompany.set(null);
      this.divisions.set([]);
      this.jobTitles.set([]);
      this.organisationalLevels.set([]);
      return;
    }

    this.loading.set(true);
    this.companyApiService.getCompany(companyId).subscribe({
      next: (company) => {
        this.selectedCompany.set(company);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });

    this.companyApiService.getCompanyDivisions({ companyId, includeInactive: true }).subscribe({
      next: (divisions) => this.divisions.set(divisions),
      error: () => undefined,
    });

    this.companyApiService.getCompanyJobTitles({ companyId, includeInactive: true }).subscribe({
      next: (jobTitles) => this.jobTitles.set(jobTitles),
      error: () => undefined,
    });

    this.companyApiService.getCompanyOrganisationalLevels({ companyId, includeInactive: true }).subscribe({
      next: (organisationalLevels) => this.organisationalLevels.set(organisationalLevels),
      error: () => undefined,
    });
  }

  createCompany(request: CompanyRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.createCompany(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.loadCompanies();
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateCompany(companyId: string, request: CompanyRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.updateCompany(companyId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.loadCompanies();
        if (this.selectedCompanyId() === companyId) {
          this.selectCompany(companyId);
        }
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateCompany(companyId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.companyApiService.deactivateCompany(companyId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.loadCompanies();
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateCompany(companyId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.companyApiService.activateCompany(companyId).subscribe({
      next: () => {
        this.activating.set(false);
        this.loadCompanies();
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }

  createDivision(request: CompanyNamedLookupRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.createCompanyDivision(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedCompany(request.companyId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateDivision(divisionId: string, request: CompanyNamedLookupRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.updateCompanyDivision(divisionId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedCompany(request.companyId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateDivision(divisionId: string, companyId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.companyApiService.deactivateCompanyDivision(divisionId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.refreshSelectedCompany(companyId);
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateDivision(divisionId: string, companyId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.companyApiService.activateCompanyDivision(divisionId).subscribe({
      next: () => {
        this.activating.set(false);
        this.refreshSelectedCompany(companyId);
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }

  createJobTitle(request: CompanyNamedLookupRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.createCompanyJobTitle(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedCompany(request.companyId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateJobTitle(jobTitleId: string, request: CompanyNamedLookupRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.updateCompanyJobTitle(jobTitleId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedCompany(request.companyId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateJobTitle(jobTitleId: string, companyId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.companyApiService.deactivateCompanyJobTitle(jobTitleId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.refreshSelectedCompany(companyId);
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateJobTitle(jobTitleId: string, companyId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.companyApiService.activateCompanyJobTitle(jobTitleId).subscribe({
      next: () => {
        this.activating.set(false);
        this.refreshSelectedCompany(companyId);
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }

  createOrganisationalLevel(request: CreateCompanyOrganisationalLevelRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.createCompanyOrganisationalLevel(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedCompany(request.companyId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  updateOrganisationalLevel(levelId: string, request: UpdateCompanyOrganisationalLevelRequest, onSuccess: () => void): void {
    this.saving.set(true);
    this.companyApiService.updateCompanyOrganisationalLevel(levelId, request).subscribe({
      next: () => {
        this.saving.set(false);
        this.refreshSelectedCompany(request.companyId);
        onSuccess();
      },
      error: () => this.saving.set(false),
    });
  }

  deactivateOrganisationalLevel(levelId: string, companyId: string, onSuccess: () => void): void {
    this.deactivating.set(true);
    this.companyApiService.deactivateCompanyOrganisationalLevel(levelId).subscribe({
      next: () => {
        this.deactivating.set(false);
        this.refreshSelectedCompany(companyId);
        onSuccess();
      },
      error: () => this.deactivating.set(false),
    });
  }

  activateOrganisationalLevel(levelId: string, companyId: string, onSuccess: () => void): void {
    this.activating.set(true);
    this.companyApiService.activateCompanyOrganisationalLevel(levelId).subscribe({
      next: () => {
        this.activating.set(false);
        this.refreshSelectedCompany(companyId);
        onSuccess();
      },
      error: () => this.activating.set(false),
    });
  }
}
